import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";

//MUI
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { Collapse, useTheme, Grid, Typography, Fade, SxProps } from "@mui/material";

//MUI Icons
import LayersIcon from "@mui/icons-material/Layers"

//OpenLayers
import OlBaseLayer from "ol/layer/Base";

export type BaseLayerSwitcherUIProps = {
  className?: string;
  layers: Array<OlBaseLayer>
  isPublic?: boolean;
  isMini?: boolean;
  mapId?: number;
}

const BaseLayerSwitcherUI = React.forwardRef<HTMLDivElement, BaseLayerSwitcherUIProps>((props, ref) => {
  const theme = useTheme()
  const { t } = useTranslation();

  interface styleType {
    [key: string]: SxProps;
  }

  const privateStyles = {
    Box: {
      bgcolor: theme.palette.background.paper, 
      width: 70,
      marginTop: "3px",
      borderWidth: "2px",
      borderStyle: "solid",
      borderColor: theme.palette.background.paper,
      borderRadius: '5px'
    },
    layerBox: {
      width: 66,
      '&:hover': {
        ".image-container": {
          borderWidth: "2px",
          borderStyle: "solid",
          borderColor: theme.palette.primary.main
        },
        a: {
          backgroundColor: theme.palette.primary.main,
          color: 'white',
          borderWidth: "2px",
          borderStyle: "solid",
          borderColor: theme.palette.primary.main,
          borderRadius: '5px'
        }
      },
    },
    layerBoxSelected: {
      width: 66,
      ".image-container": {
        borderWidth: "2px",
        borderStyle: "solid",
        borderColor: theme.palette.primary.main
      },
      a: {
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        borderWidth: "2px",
        borderStyle: "solid",
        borderColor: theme.palette.primary.main,
        borderRadius: '5px'
      }
    }
  }

  const miniStyles: styleType  = {
    layerBox: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: 62,
      '&:hover': {
        ".image-container": {
          borderWidth: "2px",
          borderStyle: "solid",
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main
        },
        ".layer-title": {
          color: theme.palette.primary.main
        }
      },
    },
    layerBoxSelected: {
      color: theme.palette.primary.main,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: 62,
      ".image-container": {
        borderWidth: "2px",
        borderStyle: "solid",
        borderColor: theme.palette.primary.main
      }
    }
  }

  const publicStyles = {
    Box: {
      width: 70,
      marginTop: "3px",
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor: theme.palette.primary.main,
      borderRadius: "10px",
    },
    layerBox: {
      '&:hover': {
        ".image-container": {
          borderWidth: "2px",
          borderStyle: "solid",
          borderRadius: "10px",
          borderColor: theme.palette.primary.main,
          boxShadow: 4,
          cursor: "pointer"
        },
        a: {
          color: theme.palette.primary.main,
        }
      },
      ".image-container": {
        borderWidth: "2px",
        borderStyle: "solid",
        borderRadius: "10px",
        borderColor: "#AAAAAA",
      },
    },
    layerBoxSelected: {
      '&:hover': {
        ".image-container": {
          borderWidth: "2px",
          borderStyle: "solid",
          borderRadius: "10px",
          borderColor: theme.palette.primary.main,
          boxShadow: 4,
          cursor: "pointer"
        }},
    
      ".image-container": {
        borderWidth: "2px",
        borderStyle: "solid",
        borderRadius: "10px",
        borderColor: theme.palette.primary.main,
        boxShadow: 2
      },
      a: {
        color: theme.palette.primary.main,
      }
    }
  }

  const { className, layers, isPublic, isMini, mapId } = props;

  const [selectedLayer, setSelectedLayer] = useState<null | string>(null)
  const [open, setOpen] = useState<boolean>(false)

  useEffect(() => {
    if (layers.length) {
      layers.forEach((l) => {
        if(l.getVisible()) setSelectedLayer(l.getProperties().id)
      })
    }
  }, [layers])

  const handleClick = (evt: React.MouseEvent<HTMLElement>) => {
    setOpen(!open)
  }

  const handleChangeLayer = (layer: OlBaseLayer, layersArray: OlBaseLayer[]) => {
    layersArray.forEach((v) => v.setVisible(false));
    layer.setVisible(!layer.getVisible());
    setSelectedLayer(layer.getProperties().id);

    const mapLayerId = mapId || 1;

    try {
      const storedBaseLayers = localStorage.getItem("mapbaselayers");
      const parsedBaseLayers = storedBaseLayers ? JSON.parse(storedBaseLayers) : {};
      parsedBaseLayers[mapLayerId] = layer.get("code");
      localStorage.setItem("mapbaselayers", JSON.stringify(parsedBaseLayers));
    } catch (error) {
      // In case of an error (e.g., invalid JSON in localStorage), initialize a new entry
      const newLocalStorageEntry: { [key: number]: string } = {};
      newLocalStorageEntry[mapLayerId] = layer.get("code");
      localStorage.setItem("mapbaselayers", JSON.stringify(newLocalStorageEntry));
    }
  };

  return (
    <>
      {!isPublic ?
      !isMini ? 
      <div ref={ref} id="base-layer-switcher" 
        className={clsx(className, "ol-unselectable", "ol-control")} 
        style={{top: "3em", right: "0.5em", width: 70, height: 70}}
      >
        <Button onClick={handleClick} style={{width: "70px", height: "70px"}}>
          <LayersIcon style={{width: 40, height: 40}} />
        </Button>
        <Box >
          <Collapse in={open}>
            <Box sx={privateStyles.Box} >
              <Stack spacing={1} sx={{wordBreak: "break-word"}}>
                {layers.map((l, index, array) => 
                  <Box 
                    key={index}
                    textAlign={"center"} 
                    sx={l.getProperties().id === selectedLayer ? privateStyles.layerBoxSelected : privateStyles.layerBox} 
                    onClick={(e)=>handleChangeLayer(l, array)}
                  >
                    <Box className={"image-container"} sx={{height: 66, width: 66}}>
                      <img src={l.getProperties().preview} style={{width: 62}} />
                    </Box>
                    <a style={{fontSize: 14}}>{t(l.get("title_ttoken"))}</a>
                  </Box>
                )}
              </Stack>
            </Box>
          </Collapse>
        </Box>
      </div>
      : <>
      <div ref={ref} id="base-layer-switcher-wrapper" 
        style={{position: "absolute", top: "3em", right: "0.5em", minHeight: 34, minWidth: 34, display: "flex", alignItems: "flex-end", flexDirection: "column", pointerEvents: "none"}}
      > 
        <div ref={ref} id="base-layer-switcher" 
        className={clsx(className, "ol-unselectable", "ol-control")} 
        >
          <Button onClick={handleClick} style={{width: "34px", height: "34px", pointerEvents: "auto" }}>
            <LayersIcon style={{width: 30, height: 30}} />
          </Button>
        </div>
        <Box sx={{
          position: "relative",
          top: "38px"
        }}>
          <Fade in={open}>
            <Grid columns={10} container sx={{wordBreak: "break-word", backgroundColor: "rgba(120,120,120,0.3)",borderRadius: "3px", padding:"3px", pointerEvents: open ? "auto" : "none"}} width={"fit-content"}>
              {layers.map((l, index, array) => 
                <Grid 
                  size={2}
                  key={index}
                  textAlign={"center"} 
                  sx={l.getProperties().id === selectedLayer ? miniStyles.layerBoxSelected : miniStyles.layerBox} 
                  onClick={(e)=>handleChangeLayer(l, array)}
                >
                  <Box className={"image-container"} sx={{height: 58, width: 58, transition: "border-color 0.2s",}}>
                    <img src={l.getProperties().preview} style={{width: 54}} />
                  </Box>
                  <a className={"layer-title"} style={{fontSize: 12, transition: "color 0.2s", fontWeight: "bold"}}>{t(l.get("title_ttoken"))}</a>
                </Grid>
              )}
            </Grid>
          </Fade>
        </Box>
      </div>
      </>
    
    : 
      <Grid 
        container
        columns={12}
        rowSpacing={"15px"} 
      >
        {layers.map((l, index, array) => 
                <Grid 
                  size={6}
                  display={"flex"}
                  rowGap={"6px"}
                  flexDirection={"column"}
                  alignItems={"center"}
                  key={index}
                  textAlign={"center"} 
                  sx={l.getProperties().id === selectedLayer ? publicStyles.layerBoxSelected : publicStyles.layerBox} 
                  onClick={(e)=>handleChangeLayer(l, array)}
                 >
                  <Box className={"image-container"} sx={{height: 66, width: 66}}>
                    <img src={l.getProperties().preview} style={{width: 62, borderRadius: "10px"}} />
                  </Box>
                  <a style={{fontSize: 14, width: "fit-content"}}>{t(l.get("title_ttoken"))}</a>
                </Grid>
              )}
      </Grid>}
    </>
  );
});

export default BaseLayerSwitcherUI;
